import React from 'react';
import { ChevronLeft, Loader2, Printer } from 'lucide-react';
import { FormState, NaturezaDespesa } from '../types';
import { DIRETRIZES } from '../constants';
import { justDisplayText } from '../utils/justificativa';

type Diretriz = (typeof DIRETRIZES)[number];
type Objetivo = Diretriz['objetivos'][number];

interface PlanoDocumentoProps {
  formData: FormState;
  selectedDiretriz?: Diretriz | null;
  selectedObjetivo?: Objetivo | null;
  dynamicNaturezas: NaturezaDespesa[];
  justificativaAlteradaEm: string | null;
  getPortariaForPrograma: (programa: string) => string;
  isSending: boolean;
  handleGeneratePDF: () => void;
  setShowDocument: (mostrar: boolean) => void;
}

// Documento oficial do Plano de Trabalho (A4), base do PDF gerado
export const PlanoDocumento: React.FC<PlanoDocumentoProps> = ({
  formData, selectedDiretriz, selectedObjetivo, dynamicNaturezas, justificativaAlteradaEm,
  getPortariaForPrograma, isSending, handleGeneratePDF, setShowDocument
}) => {
  const totalMetas = formData.acoesServicos.reduce((sum, acao) => sum + parseFloat(acao.valor.replace(/\./g, '').replace(',', '.') || 0), 0);
  
  return (
    <div className="min-h-screen bg-gray-100 p-8 print:p-0 print:bg-white">
      <div id="pdf-document" className="max-w-[210mm] mx-auto bg-white min-h-[297mm] print:min-h-[297mm] print:max-w-[210mm] print:w-[210mm] shadow-2xl print:shadow-none overflow-hidden">
        
        {/* ======== CABEÇALHO INSTITUCIONAL ======== */}
        <div className="border-b-4 border-red-700 pt-12 px-16 pb-8 print:pt-10 print:px-12 print:pb-6">
          {/* Linha superior com logo e info */}
          <div className="flex justify-center items-center mb-6">
            <img src="/img/brasao-1.png" alt="Governo de São Paulo" className="h-20 w-auto print:h-16" />
          </div>

          {/* Título principal expandido */}
          <div className="text-center border-t-2 border-gray-300 border-b-4 border-red-700 py-8 print:py-6">
            <h1 className="text-3xl font-black text-gray-900 uppercase tracking-tight print:text-2xl mb-3 leading-tight">
              Plano de Trabalho
            </h1>
            <h2 className="text-lg font-bold text-red-700 uppercase tracking-widest print:text-base mb-2">
              {getPortariaForPrograma(formData.emenda.programa)}
            </h2>
            {!formData.emenda.programa.includes('PORTARIA 10.169') && (
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide print:text-[11px]">
                Emendas Parlamentar 2026
              </p>
            )}
          </div>

          {/* Info protocolo */}
          <div className="flex justify-between items-center mt-6 print:mt-4">
            <div className="text-[11px] text-gray-700">
              <p><span className="font-bold">Sistema:</span> Plano de Trabalho - SES/SP</p>
            </div>
            <div className="text-[11px] text-gray-700 text-right">
              <p><span className="font-bold">Data de Emissão:</span> {new Date().toLocaleDateString('pt-BR')}</p>
            </div>
          </div>
        </div>

        {/* ======== CORPO DO DOCUMENTO ======== */}
        <div className="px-16 py-12 print:px-12 print:py-8 text-gray-900 text-sm print:text-[13px] print:leading-relaxed">
          
          {/* SEÇÃO 1: IDENTIFICAÇÃO GERAL */}
          <section className="mb-10 print:mb-8 break-inside-avoid">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-8 h-8 bg-red-700 text-white font-black text-xs rounded-sm print:rounded-none print:w-7 print:h-7 print:text-[11px]">01</div>
              <h2 className="text-sm font-black uppercase tracking-widest text-gray-900 print:text-[13px]">Identificação Geral</h2>
            </div>
            <div className="border-t border-gray-300 pt-4 pl-11">
              <div className="grid grid-cols-2 gap-x-8 gap-y-5 print:gap-x-6 print:gap-y-4">
                <div>
                  <label className="block text-[10px] font-bold uppercase text-gray-600 tracking-widest mb-1">Programa de Saúde</label>
                  <p className="text-sm font-semibold text-gray-900 border-b border-gray-400 pb-1 print:border-b-0">{formData.emenda.programa}</p>
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase text-gray-600 tracking-widest mb-1">Parlamentar Autor</label>
                  <p className="text-sm font-semibold text-gray-900 border-b border-gray-400 pb-1 print:border-b-0">{formData.emenda.parlamentar || '—'}</p>
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase text-gray-600 tracking-widest mb-1">Número da Emenda</label>
                  <p className="text-sm font-semibold text-gray-900 border-b border-gray-400 pb-1 print:border-b-0 font-mono">{formData.emenda.numero || '—'}</p>
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase text-gray-600 tracking-widest mb-1">Dotação Prevista (R$)</label>
                  <p className="text-sm font-bold text-red-700 border-b-2 border-red-700 pb-1 print:border-b-0 font-mono">{formData.emenda.valor || '0,00'}</p>
                </div>
              </div>

              {/* Entidade Responsável - linha cheia */}
              <div className="mt-5 print:mt-4">
                <label className="block text-[10px] font-bold uppercase text-gray-600 tracking-widest mb-1">Entidade Responsável</label>
                <div className="border-b border-gray-400 pb-1 print:border-b-0">
                  <p className="text-sm font-semibold text-gray-900">{formData.beneficiario.nome || '—'}</p>
                  <p className="text-xs text-gray-700 font-mono">CNPJ: {formData.beneficiario.cnpj || '—'}</p>
                  {formData.beneficiario.cnes && <p className="text-xs text-gray-700 font-mono">CNES: {formData.beneficiario.cnes}</p>}
                  {formData.beneficiario.contaBancaria && <p className="text-xs text-gray-700 font-mono">Conta Bancária: {formData.beneficiario.contaBancaria}</p>}
                </div>
              </div>
            </div>
          </section>

          {/* SEÇÃO 2: DIRETRIZES, OBJETIVOS E METAS PLANEJADAS */}
          <section className="mb-10 print:mb-8 break-inside-avoid">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-8 h-8 bg-red-700 text-white font-black text-xs rounded-sm print:rounded-none print:w-7 print:h-7 print:text-[11px]">02</div>
              <h2 className="text-sm font-black uppercase tracking-widest text-gray-900 print:text-[13px]">Planejamento Estratégico</h2>
            </div>
            <div className="border-t border-gray-300 pt-4 pl-11">
              <p className="text-xs text-gray-600 mb-4">Diretrizes, objetivos e metas selecionadas</p>
              
              {selectedDiretriz ? (
                <div className="space-y-4">
                  {/* Diretriz */}
                  <div className="border-l-4 border-red-700 pl-4">
                    <label className="block text-[10px] font-bold uppercase text-gray-600 tracking-widest mb-1">Diretriz Estratégica</label>
                    <p className="text-sm font-semibold text-gray-900">{selectedDiretriz.titulo}</p>
                  </div>
                  
                  {/* Objetivo */}
                  {selectedObjetivo && (
                    <div className="border-l-4 border-blue-500 pl-4">
                      <label className="block text-[10px] font-bold uppercase text-gray-600 tracking-widest mb-1">Objetivo Específico</label>
                      <p className="text-sm font-semibold text-gray-900">{selectedObjetivo.titulo}</p>
                    </div>
                  )}
                  
                  {/* Metas */}
                  {selectedObjetivo && selectedObjetivo.metas && formData.planejamento.metaIds.length > 0 && (
                    <div className="border-l-4 border-green-600 pl-4">
                      <label className="block text-[10px] font-bold uppercase text-gray-600 tracking-widest mb-2">Metas Relacionadas</label>
                      <ul className="space-y-2">
                        {selectedObjetivo.metas
                          .filter(meta => formData.planejamento.metaIds.includes(meta.id))
                          .map((meta, idx) => (
                          <li key={idx} className="text-xs text-gray-800 flex items-start gap-2">
                            <span className="text-green-600 font-bold mt-0.5">•</span>
                            <span>{meta.descricao}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-500 italic">Nenhuma diretriz selecionada</p>
              )}
            </div>
          </section>

          {/* SEÇÃO 3: METAS QUANTITATIVAS */}
          <section className="mb-10 print:mb-8 break-inside-avoid">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-8 h-8 bg-red-700 text-white font-black text-xs rounded-sm print:rounded-none print:w-7 print:h-7 print:text-[11px]">03</div>
              <h2 className="text-sm font-black uppercase tracking-widest text-gray-900 print:text-[13px]">Metas Quantitativas</h2>
            </div>
            <div className="border-t border-gray-300 pt-4 pl-11">
              <p className="text-xs text-gray-600 mb-4">Ações, serviços e valores detalhados</p>
              
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-200 print:bg-gray-100">
                    <th className="border border-gray-400 px-3 py-2 text-left font-black uppercase text-gray-900 text-[10px]">Grupo de Ação</th>
                    <th className="border border-gray-400 px-3 py-2 text-left font-black uppercase text-gray-900 text-[10px]">Ação Específica</th>
                    <th className="border border-gray-400 px-3 py-2 text-right font-black uppercase text-gray-900 text-[10px]">Valor (R$)</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.acoesServicos.length > 0 ? (
                    <>
                      {formData.acoesServicos.map((acao, i) => (
                        <tr key={i} className="border-b border-gray-300">
                          <td className="border border-gray-300 px-3 py-2 text-xs font-medium text-gray-900">{acao.categoria}</td>
                          <td className="border border-gray-300 px-3 py-2 text-xs font-medium text-gray-900">{acao.item}</td>
                          <td className="border border-gray-300 px-3 py-2 text-right text-xs font-mono font-bold text-gray-900">R$ {acao.valor}</td>
                        </tr>
                      ))}
                      <tr className="bg-gray-100 print:bg-white font-bold">
                        <td colSpan={2} className="border border-gray-400 px-3 py-2 text-right uppercase text-xs font-black text-gray-900">Total Geral:</td>
                        <td className="border border-red-700 border-l-4 px-3 py-2 text-right font-mono text-sm text-red-700 font-black">R$ {totalMetas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td colSpan={3} className="border border-gray-300 px-3 py-4 text-center text-gray-500 italic">Nenhuma meta registrada</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* SEÇÃO 4: JUSTIFICATIVA TÉCNICA */}
          <section className="mb-10 print:mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-8 h-8 bg-red-700 text-white font-black text-xs rounded-sm print:rounded-none print:w-7 print:h-7 print:text-[11px]">04</div>
              <h2 className="text-sm font-black uppercase tracking-widest text-gray-900 print:text-[13px]">Justificativa Técnica</h2>
            </div>
            <div className="border-t border-gray-300 pt-4 pl-11">
              <div className="border-l-4 border-red-700 bg-gray-50 print:bg-white pl-4 pr-3 py-3 text-xs leading-relaxed text-gray-900 text-justify whitespace-pre-wrap break-words">
                {justDisplayText(formData.justificativa) || '—'}
              </div>
              {justificativaAlteradaEm && (
                <p className="mt-2 text-[10px] text-gray-400 italic text-right">
                  Justificativa alterada em {new Date(justificativaAlteradaEm).toLocaleDateString('pt-BR')} às {new Date(justificativaAlteradaEm).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
          </section>

          {/* SEÇÃO 5: INDICADORES QUALITATIVOS */}
          <section className="mb-10 print:mb-8 break-inside-avoid">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-8 h-8 bg-red-700 text-white font-black text-xs rounded-sm print:rounded-none print:w-7 print:h-7 print:text-[11px]">05</div>
              <h2 className="text-sm font-black uppercase tracking-widest text-gray-900 print:text-[13px]">Indicadores Qualitativos <span className="text-xs font-normal">(Opcional)</span></h2>
            </div>
            <div className="border-t border-gray-300 pt-4 pl-11">
              <p className="text-xs text-gray-600 mb-4">Indicadores de qualidade e acompanhamento</p>
              
              {formData.metasQualitativas.length > 0 ? (
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-gray-200 print:bg-gray-100">
                      <th className="border border-gray-400 px-3 py-2 text-left font-black uppercase text-gray-900 text-[10px]">Indicador de Qualidade</th>
                      <th className="border border-gray-400 px-3 py-2 text-left font-black uppercase text-gray-900 text-[10px]">Meta / Descrição</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.metasQualitativas.map((meta, i) => (
                      <tr key={i} className="border-b border-gray-300">
                        <td className="border border-gray-300 px-3 py-2 text-xs font-medium text-gray-900">{meta.meta}</td>
                        <td className="border border-gray-300 px-3 py-2 text-xs text-gray-800">{meta.valor}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-xs text-gray-500 italic">Nenhum indicador qualitativo registrado</p>
              )}
            </div>
          </section>
          {formData.naturezasDespesa.length > 0 && (
            <section className="mb-10 print:mb-8 break-inside-avoid">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center justify-center w-8 h-8 bg-red-700 text-white font-black text-xs rounded-sm print:rounded-none print:w-7 print:h-7 print:text-[11px]">06</div>
                <h2 className="text-sm font-black uppercase tracking-widest text-gray-900 print:text-[13px]">Execução Financeira</h2>
              </div>
              <div className="border-t border-gray-300 pt-4 pl-11">
                <p className="text-xs text-gray-600 mb-6">Classificação e distribuição de despesas por natureza</p>
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-gray-200 print:bg-gray-100">
                      <th className="border border-gray-400 px-3 py-2 text-left font-black uppercase text-gray-900 text-[10px]">Natureza de Despesa</th>
                      <th className="border border-gray-400 px-3 py-2 text-right font-black uppercase text-gray-900 text-[10px]">Valor (R$)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.naturezasDespesa.map((despesa, i) => (
                      <tr key={i} className="border-b border-gray-300 hover:bg-gray-50 print:hover:bg-white">
                        <td className="border border-gray-300 px-3 py-2 text-xs font-medium text-gray-900">{despesa.codigo} - {dynamicNaturezas.find(n => n.codigo === despesa.codigo)?.descricao}</td>
                        <td className="border border-gray-300 px-3 py-2 text-right text-xs font-mono font-bold">R$ {despesa.valor}</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-100 print:bg-white font-bold">
                      <td className="border border-gray-400 px-3 py-2 text-right uppercase text-xs font-black text-gray-900">Total Planejado:</td>
                      <td className="border border-red-700 border-l-4 px-3 py-2 text-right font-mono text-sm text-red-700 font-black">
                        R$ {formData.naturezasDespesa.reduce((sum, item) => {
                          const value = parseFloat(item.valor.replace(/[^\d,-]/g, '').replace(',', '.')) || 0;
                          return sum + value;
                        }, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>

        {/* ======== SEÇÃO 7: ASSINATURA ======== */}
        <div className="px-16 py-12 print:px-12 print:py-8 print:page-break-before-avoid" style={{pageBreakInside: 'avoid'}}>
        <section className="mb-10 print:mb-8" style={{pageBreakInside: 'avoid'}}>
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center w-8 h-8 bg-red-700 text-white font-black text-xs rounded-sm print:rounded-none print:w-7 print:h-7 print:text-[11px]">07</div>
            <h2 className="text-sm font-black uppercase tracking-widest text-gray-900 print:text-[13px]">Responsável pela Assinatura</h2>
          </div>
          <div className="border-t border-gray-300 pt-4 pl-11">
            <div className="space-y-12 print:space-y-10">
              {/* Espaço para assinatura */}
              <div className="text-center">
                <div className="mb-3 h-20 border-b-2 border-gray-800 print:border-gray-600 print:h-16"></div>
                <p className="text-xs font-bold text-gray-700 uppercase tracking-widest">Assinatura</p>
              </div>

              {/* Nome e Data */}
              <div className="grid grid-cols-2 gap-12 print:gap-8">
                <div>
                  <p className="text-xs text-gray-600 font-bold mb-3 uppercase tracking-widest">Responsável:</p>
                  <p className="text-sm font-semibold text-gray-900 border-b-2 border-gray-400 pb-3 print:pb-2 print:border-gray-600 print:text-xs">{formData.responsavelAssinatura || '_'.repeat(40)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 font-bold mb-3 uppercase tracking-widest">Data:</p>
                  <p className="text-sm font-semibold text-gray-900 border-b-2 border-gray-400 pb-3 print:pb-2 print:border-gray-600 print:text-xs">_____  /  _____  /  _________</p>
                </div>
              </div>
            </div>
          </div>
        </section>
        </div>

        {/* ======== RODAPÉ INSTITUCIONAL ======== */}
        <div className="border-t-4 border-red-700 px-16 py-8 print:px-12 print:py-6 bg-gradient-to-b from-gray-50 to-white print:bg-white text-xs text-gray-700">
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white print:bg-white border border-gray-200 print:border-gray-300 p-3 rounded-lg print:rounded-none">
              <p className="text-[10px] font-black uppercase text-gray-600 mb-2 tracking-widest">Órgão Emissor</p>
              <p className="font-bold text-gray-900 text-xs">Secretaria de Estado da Saúde</p>
              <p className="text-[10px] text-gray-600">SES - São Paulo</p>
            </div>
            <div className="bg-white print:bg-white border border-gray-200 print:border-gray-300 p-3 rounded-lg print:rounded-none text-center">
              <p className="text-[10px] font-black uppercase text-gray-600 mb-2 tracking-widest">Período</p>
              <p className="font-bold text-red-700 text-lg">2026</p>
            </div>
            <div className="bg-white print:bg-white border border-gray-200 print:border-gray-300 p-3 rounded-lg print:rounded-none text-right">
              <p className="text-[10px] font-black uppercase text-gray-600 mb-2 tracking-widest">Gerado em</p>
              <p className="font-mono text-gray-900 text-xs">{new Date().toLocaleDateString('pt-BR')}</p>
              <p className="font-mono text-gray-700 text-[10px]">{new Date().toLocaleTimeString('pt-BR', {hour: '2-digit', minute:'2-digit'})}</p>
            </div>
          </div>
          <div className="border-t-2 border-gray-300 pt-6 mt-6 text-center">
            <p className="text-[10px] font-bold text-gray-700 mb-2 uppercase tracking-widest">DOCUMENTO OFICIAL - PLANO DE TRABALHO</p>
            <p className="text-[9px] text-gray-600 leading-relaxed">
              Documento oficial da Secretaria de Estado da Saúde de São Paulo, gerado pelo Sistema de Gestão de Planos de Trabalho.
            </p>
            <p className="text-[9px] text-gray-600 mt-2">
              Destinado a arquivamento, auditoria, controle externo e conformidade com as determinações da {getPortariaForPrograma(formData.emenda.programa)}.
            </p>
          </div>
        </div>

      </div>

      {/* ======== BOTÕES DE AÇÃO (fora do PDF) ======== */}
      <div className="flex justify-center gap-4 p-6 bg-gray-100 border-t border-gray-300 flex-wrap print:hidden">
        <button 
          onClick={() => {
            setShowDocument(false);
          }}
          className="h-12 px-6 bg-white border border-slate-300 text-slate-900 font-semibold text-sm rounded-xl hover:bg-slate-50 transition-colors flex items-center gap-2"
        >
          <ChevronLeft className="w-4 h-4" aria-hidden="true" /> Voltar ao formulário
        </button>
        <button
          onClick={() => handleGeneratePDF()}
          disabled={isSending}
          className="h-12 px-6 bg-[#0B3B8C] text-white font-bold text-sm rounded-xl hover:bg-[#08162D] transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {isSending ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Salvando...</> : <><Printer className="w-4 h-4" aria-hidden="true" /> Visualizar e salvar como PDF</>}
        </button>
      </div>
    </div>
  );
};
